import jwt_decode from 'jwt-decode';
import { AnalyticsContext, useAnalyticsEvents } from '@atlaskit/analytics-next';
import {
  FeatureGateEnvironment,
  FeatureGatesInitializationProvider
} from '@atlassiansox/bxp-web-statsig-client';
import { Environment, Storage, Url } from '@atlassiansox/bxpkit-core';
import React, { PropsWithChildren, useEffect } from 'react';
import { Provider } from 'react-redux';
import { ErrorLocale } from './layouts/error-locale';
import {
  store as GlobalStore,
  updateFeatureFlags,
  updateProductKeys,
  useAppDispatch
} from './redux';

import { markSignUpLoadingStart } from './metrics';

import { SignUpLayoutContainer as LegacySignUpLayoutContainer } from './layouts/legacy/view/sign-up-layout-container';

import { FirstImpressionsScreen } from './layouts/first-impressions-screen';

import {
  ExperimentScreensSwitch,
  showExperimentScreens
} from './layouts/experiment-screens/experiment-screens-switch';

import { DevopsScreen } from './layouts/devops-screen';

import {
  getProductKeys,
  getTintinMvpEnabledOverrideValue
} from './layouts/legacy/view/utility';

import { ProductKeysType } from '@atlassiansox/bxpkit-core';

import {
  SignUpConfigContextProvider,
  useSignUpFormConfig,
  useUserInfo
} from './hook';

import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { IntlProvider } from 'react-intl';
import { SignUpGlobalConfigurationContextProvider } from './hook/use-sign-up-form-global-configuration';
import { DevopsScreenStandalone } from './layouts/devops-screen-standalone';
import { SimpleSignupScreen } from './layouts/simple-signup-screen';
import {
  AnalyticsTrackType,
  DevopsImageUrlsType,
  FeatureFlags,
  FirstImpressionsImageUrlsType,
  GlobalConfiguration,
  LabelsType,
  LocaleType,
  ProductBundleItems,
  ProductDataType,
  SimpleSignupImageUrlsType
} from './model';
import { reactIntlErrorHandler } from './utility';

const blockedLocale = ['RU', 'BY'];
export type LayoutType =
  | 'default'
  | 'standalone'
  | 'standalone-v2'
  | 'devops'
  | 'devops-v2'
  | 'devops-v2-standalone'
  | 'first-impressions'
  | 'first-impressions-v2'
  | 'simple-signup';

export type ExperimentScreensFlags = {
  readonly enableSlackPromoConfluence: boolean;
};

export type SignUpLayoutContainerInitialProps = {
  readonly allowEnableAutogenSiteName: boolean;
  readonly allowSignupWithGoogle: boolean;
  readonly allowSignupWithAllSocialOptions: boolean;
  readonly enableFreeBreachTest: boolean;
  readonly showSocialSignupForm: boolean;
  readonly socialContinueUrl: string;
  readonly loadingImageSrc: string;
  readonly errorImageSrc: string;
  readonly productData: { [key: string]: ProductDataType };
  readonly locale: LocaleType;
  readonly analyticsTrack: AnalyticsTrackType;
  readonly labels: LabelsType;
  readonly layout?: LayoutType;
  readonly productOverride?: string;
  readonly bundleOverride?: string;
  readonly editionOverride?: string;
  readonly performanceStartTime?: number;
  readonly firstImpressionsImageUrls: FirstImpressionsImageUrlsType;
  readonly devopsImageUrls: DevopsImageUrlsType;
  readonly siteRenamePageUrl?: string;
  readonly simpleSignupImageUrls: SimpleSignupImageUrlsType;
  readonly enableSkipRecaptcha?: boolean;
  readonly featureFlags: FeatureFlags;
  readonly globalConfiguration?: GlobalConfiguration;
  readonly bundleMap?: ProductBundleItems;
  readonly experimentScreensFlags?: ExperimentScreensFlags;
  readonly customSignupContext?: string;
};

export type SignUpLayoutContainerGeneratedProps = {
  readonly productKeys: ProductKeysType[];
  readonly templateId: string;
  readonly isMigrationSignup: boolean;
  readonly queryParameters: { [key: string]: string };
  readonly intlReactOnError: (error: string) => void;
};

export type SignUpLayoutContainerProps = SignUpLayoutContainerInitialProps &
  SignUpLayoutContainerGeneratedProps;

function SignUpLayoutContainerSwitch(props: SignUpLayoutContainerProps) {
  const userInfo = useUserInfo();
  if (blockedLocale.includes(props.locale.locale)) {
    return <ErrorLocale locale={props.locale.locale} />;
  }
  if (showExperimentScreens(props, userInfo)) {
    return <ExperimentScreensSwitch {...props} />;
  }
  switch (props.layout) {
    case 'first-impressions-v2':
      return <FirstImpressionsScreen {...props} />;
    case 'simple-signup':
      return <SimpleSignupScreen {...props} />;
    case 'devops-v2':
      return <DevopsScreen {...props} />;
    case 'devops-v2-standalone':
      return <DevopsScreenStandalone {...props} />;
    default:
      return <LegacySignUpLayoutContainer {...props} />;
  }
}

function SignUpAnalyticsContext({
  children,
  ...props
}: PropsWithChildren<SignUpLayoutContainerInitialProps>) {
  const { bundle, product, edition } = useSignUpFormConfig();

  const defaultAnalyticsData = {
    schemaVersion: 1,
    event: 'event', // event verb, in past tense (eg, "clicked")
    eventType: 'UI',
    eventComponent: 'signUp', // name of component being interacted with, in camelCase
    eventContainer: 'signUp', // container, in camelCase; eg, component that contains this component within a page
    eventColumn: 'signUpForm', // should be bundleInformation or signUpForm
    eventName: 'signUpEvent',
    label: '', // the text present on the component being clicked (exact text, not camelCase)
    parentLabel: '', // optional, if this is a nested click (exact text, not camelCase)
    uuid: 'signUp', // required for Magnolia events, unique id for a given component if available
    layout: props.layout,
    featureFlags: props.featureFlags,
    bundle,
    product,
    edition
  };
  return (
    <AnalyticsContext data={defaultAnalyticsData}>{children}</AnalyticsContext>
  );
}

type SignUpFormLoadedEventContainerProps = {
  readonly children: React.ReactNode;
};

function SignUpFormLoadedEventContainer({
  children
}: SignUpFormLoadedEventContainerProps) {
  const { createAnalyticsEvent } = useAnalyticsEvents();
  const userInfo = useUserInfo();
  useEffect(() => {
    if (userInfo) {
      createAnalyticsEvent({
        event: 'loaded',
        eventName: 'signUpLoaded',
        eventType: 'UI',
        eventComponent: 'signUp',
        eventColumn: 'signUp',
        isSiteNamePage: userInfo?.isLoggedIn
      }).fire();
    }
  }, [userInfo]);
  return <>{children}</>;
}

export function SignUpLayoutContainer(
  props: SignUpLayoutContainerInitialProps
) {
  markSignUpLoadingStart();

  return (
    <Provider store={GlobalStore}>
      <SignUpGlobalConfigurationContextProvider
        confirmationPageUrl={props.globalConfiguration?.confirmationPageUrl}
        checkEmailPageUrl={props.globalConfiguration?.checkEmailPageUrl}
        newUserSignupRedirectUrl={
          props.globalConfiguration?.newUserSignupRedirectUrl
        }
      >
        <SignUpReduxStoreHydrate {...props} />
      </SignUpGlobalConfigurationContextProvider>
    </Provider>
  );
}

function SignUpReduxStoreHydrate(props: SignUpLayoutContainerInitialProps) {
  const dispatch = useAppDispatch();

  const queryParameters = Url.getUrlParams() || {};
  let productKeys = getProductKeys({
    productData: props.productData,
    productOverride: props.productOverride,
    bundleOverride: props.bundleOverride,
    editionOverride: props.editionOverride,
    queryParameters
  });

  const templateId = queryParameters.hasOwnProperty('templateId')
    ? queryParameters.templateId
    : null;

  const isMigrationSignup = queryParameters.hasOwnProperty(
    'migrationSourceUuid'
  );

  const { createAnalyticsEvent } = useAnalyticsEvents();

  const intlReactOnError = (error: any) => {
    if (typeof error === 'string') {
      reactIntlErrorHandler(error, createAnalyticsEvent);
    }
  };

  useEffect(() => {
    let featureFlagsCombined = Object.assign({}, { ...props.featureFlags });
    dispatch(updateFeatureFlags(featureFlagsCombined));

    // update Redux propductKeys slice
    dispatch(updateProductKeys(productKeys));
  }, []);
  // productKeys update
  useEffect(() => {
    productKeys = getProductKeys({
      productData: props.productData,
      queryParameters,
      productOverride: props.productOverride,
      bundleOverride: props.bundleOverride
    });
    // update Redux propductKeys slice on new bundle selection
    dispatch(updateProductKeys(productKeys));
  }, [props.productOverride, props.bundleOverride]);

  const enableTintinMvp = getTintinMvpEnabledOverrideValue({
    productKeys,
    isMigrationSignup,
    enableTintinMvp: props.featureFlags.enableTintinMvp
  });

  return (
    <SignupLayoutConfigContainer
      productKeys={productKeys}
      templateId={templateId}
      isMigrationSignup={isMigrationSignup}
      queryParameters={queryParameters}
      intlReactOnError={intlReactOnError}
      {...props}
      featureFlags={{
        ...props.featureFlags,
        enableTintinMvp
      }}
    />
  );
}

function SignupLayoutConfigContainer(props: SignUpLayoutContainerProps) {
  const environment = Environment.getEnvironment();
  let decoded: { [key: string]: any };
  let sessionToken;
  const analyticsAnonymousId = (
    Storage.getCookie('ajs_anonymous_id') ?? ''
  ).replace(/\"/g, '');
  let featureGateEnvironment: FeatureGateEnvironment | undefined;
  switch (environment) {
    case 'dev':
      featureGateEnvironment = FeatureGateEnvironment.Development;
      sessionToken = Storage.getCookie('cloud.session.token.stg');
      decoded = sessionToken ? jwt_decode(sessionToken) : {};
      break;
    case 'staging':
      featureGateEnvironment = FeatureGateEnvironment.Staging;
      sessionToken = Storage.getCookie('cloud.session.token.stg');
      decoded = sessionToken ? jwt_decode(sessionToken) : {};
      break;
    case 'production':
      featureGateEnvironment = FeatureGateEnvironment.Production;
      sessionToken = Storage.getCookie('cloud.session.token');
      decoded = sessionToken ? jwt_decode(sessionToken) : {};
      break;
  }
  const atlassianAccountId = decoded?.sub || undefined;
  const atlassianOrgId = decoded?.orgId || undefined;
  // for statsig provider experiments look up and feature gate look up use useExperiment or useFeatureGate
  const identifiers = {
    analyticsAnonymousId,
    atlassianOrgId,
    atlassianAccountId
  };
  return (
    <IntlProvider onError={props.intlReactOnError}>
      <FeatureGatesInitializationProvider
        environment={
          featureGateEnvironment ?? FeatureGateEnvironment.Development
        }
        identifiers={identifiers}
      >
        <SignUpConfigContextProvider {...props}>
          <SignUpAnalyticsContext {...props}>
            <GoogleReCaptchaProvider
              useEnterprise={true}
              reCaptchaKey={Environment.reCaptchaKey}
            >
              <SignUpFormLoadedEventContainer>
                <SignUpLayoutContainerSwitch {...props} />
              </SignUpFormLoadedEventContainer>
            </GoogleReCaptchaProvider>
          </SignUpAnalyticsContext>
        </SignUpConfigContextProvider>
      </FeatureGatesInitializationProvider>
    </IntlProvider>
  );
}
